import { defineStore } from "pinia";
import { computed } from "vue";
import { t } from "@/i18n";
import {
    BookIcon,
    CalendarIcon,
    LayoutHorizontal3Icon,
    LayoutHorizontal4Icon,
    LayoutSquare2Icon,
    LayoutVertical2Icon,
    LayoutVertical4Icon,
    LayoutSliderIcon,
} from "@easy/ui/icons";
import SvgPricingLayoutBasic from "@/apps/pricing/project/onboarding/svg-previews/SvgPricingLayoutBasic.vue";
import { makeLogger } from "@/shared/consola";
import SvgPricingLayoutLoop from "@/apps/pricing/project/onboarding/svg-previews/SvgPricingLayoutLoop.vue";
import SvgPricingLayoutGrid from "@/apps/pricing/project/onboarding/svg-previews/SvgPricingLayoutGrid.vue";
import SvgPricingLayoutList from "@/apps/pricing/project/onboarding/svg-previews/SvgPricingLayoutList.vue";
import SvgPricingLayoutLinks from "@/apps/pricing/project/onboarding/svg-previews/SvgPricingLayoutLinks.vue";
import SvgPricingLayoutShared from "@/apps/pricing/project/onboarding/svg-previews/SvgPricingLayoutShared.vue";
import SvgPricingLayoutBasicBottom from "@/apps/pricing/project/onboarding/svg-previews/SvgPricingLayoutBasicBottom.vue";
import SvgPricingLayoutGridBottom from "@/apps/pricing/project/onboarding/svg-previews/SvgPricingLayoutGridBottom.vue";
import { useOnboarding } from "@/shared/projects/onboarding/useOnboarding";
import { useActions, useMutations, useState } from "@/store/vuex-composition-mappers";
import { mapThemeToPricingColors } from "@/shared/projects/prepare-theme";

const STORE_NAME = "pricing-creator";
const logger = makeLogger("🍍" + STORE_NAME);

export const usePricingCreatorStore = defineStore(STORE_NAME, () => {
    logger.trace("👋hello");

    const { pricing, categories } = useState("pricing", ["pricing", "categories"]);
    const { addCategory } = useActions("pricing", ["addCategory"]);
    const { addVariant, removeCategory } = useMutations("pricing", [
        "addVariant",
        "removeCategory",
    ]);
    const { saveSnapshot } = useActions("pricing/projects", ["saveSnapshot"]);
    const { showAlert } = useMutations(["showAlert"]);

    const {
        isCreatorShown,
        onboardState,
        previewCssVars,
        autoOpenCreator,
        toggleCreatorShown,
        updateProductInfo,
        validate,
    } = useOnboarding({
        //autoOpenRules: (nVariants) => nVariants === 0,
        initialValues: {
            layout: "basic",
            template: "empty",
            hidePrompt: true, // for now (EAS-2196)
            mode: "by-prompt", // for now (EAS-2196)
        },
    });

    onboardState.promptDescription = "use:(empty)"; // for now (EAS-2196)

    const templates = computed(() => [
        {
            value: t("pricing.templates.empty"),
            id: "empty",
        },
        {
            value: t("pricing.templates.subscription"),
            icon: CalendarIcon,
            id: "subscription",
        },
        {
            value: "E-book",
            icon: BookIcon,
            id: "ebook",
        },
    ]);

    const layouts = computed(() => [
        {
            label: t("pricing.layout.basic"),
            value: "basic",
            info: t("pricing.layout.basic_info"),
            icon: LayoutHorizontal3Icon,
            preview: SvgPricingLayoutBasic,
        },
        {
            label: t("pricing.layout.loop"),
            value: "loop",
            info: t("pricing.layout.loop_info"),
            icon: LayoutSliderIcon,
            preview: SvgPricingLayoutLoop,
        },
        {
            label: t("pricing.layout.grid"),
            value: "grid",
            info: t("pricing.layout.grid_info"),
            icon: LayoutHorizontal4Icon,
            preview: SvgPricingLayoutGrid,
        },
        {
            label: t("pricing.layout.list"),
            value: "list",
            info: t("pricing.layout.list_info"),
            icon: LayoutVertical2Icon,
            preview: SvgPricingLayoutList,
        },
        {
            label: t("pricing.layout.links"),
            value: "links",
            info: t("pricing.layout.links_info"),
            icon: LayoutVertical4Icon,
            preview: SvgPricingLayoutLinks,
        },
        {
            label: t("pricing.layout.shared"),
            value: "shared",
            info: t("pricing.layout.shared_info"),
            icon: LayoutSquare2Icon,
            preview: SvgPricingLayoutShared,
        },
        {
            label: t("pricing.layout.basicBottom"),
            value: "basicBottom",
            info: t("pricing.layout.basicBottom_info"),
            icon: LayoutHorizontal3Icon,
            preview: SvgPricingLayoutBasicBottom,
        },
        {
            label: t("pricing.layout.gridBottom"),
            value: "gridBottom",
            info: t("pricing.layout.gridBottom_info"),
            icon: LayoutHorizontal4Icon,
            preview: SvgPricingLayoutGridBottom,
        },
    ]);

    const previewComponent = computed(
        () => layouts.value.find((l) => l.value === onboardState.layout)?.preview
    );

    return {
        onboardState,
        isCreatorShown,
        templates,
        layouts,
        previewCssVars,
        previewComponent,
        links: computed(() => ({
            video: "",
            docs: "",
        })),
        autoOpenCreator,
        onboardingAction(resolveResponse) {
            return async () => {
                if (!validate()) {
                    return;
                }
                let fireResolver = resolveResponse;
                if (onboardState.mode === "by-product" && onboardState.productSelectLater) {
                    fireResolver = async () => ({
                        result: {
                            title: t("pricing.templates.sub_titile"),
                            subtitle: t("pricing.templates.sub_subtitle"),
                            categories: [
                                {
                                    name: "sample",
                                    variants: [
                                        {
                                            title: t("pricing.templates.empty_title"),
                                            desc: t("pricing.templates.empty_desc"),
                                            price: 15,
                                            promoPrice: 10,
                                            currency: "PLN",
                                            points: "+ ..1\nx ..2",
                                        },
                                    ],
                                },
                            ],
                        },
                    });
                }
                try {
                    onboardState.isLoading = true;
                    const { result } = await fireResolver();
                    const {
                        title = "",
                        subtitle = "",
                        categories: generatedCategories = [],
                    } = result;
                    const { theme, layout } = onboardState;

                    logger.debug("about to fill form resolveResponse()", { result, theme, layout });
                    if (generatedCategories.length) {
                        removeCategory(categories.value[0]);
                    }
                    for (const category of generatedCategories) {
                        const { name, variants } = category;
                        const categoryId = await addCategory(name);
                        for (const {
                            title,
                            description: desc,
                            price = 0,
                            promoPrice,
                            currency,
                            points,
                            highlight,
                        } of variants) {
                            addVariant({
                                categoryId,
                                title,
                                desc,
                                price: price,
                                promoPrice: promoPrice ? String(promoPrice) : "",
                                priceInfo: currency,
                                highlight,
                                points,
                            });
                        }
                    }
                    for (const [key, value] of Object.entries(mapThemeToPricingColors(theme))) {
                        pricing.value[key] = value;
                    }
                    pricing.value.layout = layout;
                    pricing.value.title = title || "";
                    pricing.value.subtitle = subtitle || "";
                    showAlert({
                        type: "success",
                        title: t("shared.creator.generation_success"),
                    });
                    await saveSnapshot();
                    toggleCreatorShown(false);
                } catch (e) {
                    logger.error(e);
                } finally {
                    onboardState.isLoading = false;
                }
            };
        },
        toggleCreatorShown,
        updateProductInfo,
        validate,
    };
});
